// src/stores/workspaceStore.ts
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type FocusType = "token" | "sector" | "macro" | "portfolio" | "custom" | null;
export type TimeframePreset = "24h" | "7d" | "30d" | "90d" | "180d" | "1y" | "all";

export interface WorkspaceFocus {
  type: FocusType;
  entityId: string | null;
  entityName?: string | undefined;
  /** For comparison views - multiple entities being compared */
  entities?: string[] | undefined;
}

export interface WorkspaceTimeframe {
  preset: TimeframePreset;
  /** ISO date string for custom range start */
  customStart?: string | undefined;
  /** ISO date string for custom range end */
  customEnd?: string | undefined;
}

export interface WorkspaceBenchmarks {
  primary: string;
  secondary: string;
  enabled: boolean;
}

export interface RecentFocus {
  type: FocusType;
  entityId: string;
  entityName: string;
  timestamp: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// State & Actions
// ─────────────────────────────────────────────────────────────────────────────

interface WorkspaceStoreState {
  // Core context
  focus: WorkspaceFocus;
  timeframe: WorkspaceTimeframe;
  benchmarks: WorkspaceBenchmarks;

  // History
  recentFocuses: RecentFocus[];

  // UI state
  isAssistantPanelOpen: boolean;
  assistantPanelWidth: number;

  // Assistant prefill (for "Ask AI" buttons)
  assistantPrefill: string | null;
}

interface WorkspaceStoreActions {
  // Focus actions
  setFocus: (type: FocusType, entityId: string | null, entityName?: string) => void;
  setComparisonFocus: (type: FocusType, entities: string[], label?: string) => void;
  clearFocus: () => void;

  // Timeframe actions
  setTimeframe: (preset: TimeframePreset) => void;
  setCustomTimeframe: (start: string, end: string) => void;

  // Benchmark actions
  setBenchmarks: (primary: string, secondary: string) => void;
  toggleBenchmarks: () => void;

  // Assistant panel actions
  toggleAssistantPanel: () => void;
  setAssistantPanelOpen: (open: boolean) => void;
  setAssistantPanelWidth: (width: number) => void;

  // Assistant prefill
  setAssistantPrefill: (text: string | null) => void;
  clearAssistantPrefill: () => void;

  // Open assistant with prefilled prompt
  openAssistantWithPrompt: (prompt: string) => void;
}

type WorkspaceStore = WorkspaceStoreState & WorkspaceStoreActions;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_RECENT_FOCUSES = 10;
const MIN_PANEL_WIDTH = 360;
const MAX_PANEL_WIDTH = 800;
const DEFAULT_PANEL_WIDTH = 480;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useWorkspaceStore = create<WorkspaceStore>()(
  devtools(
    persist(
      (set) => ({
        // ─── Initial State ───
        focus: {
          type: null,
          entityId: null,
          entityName: undefined,
          entities: undefined,
        },
        timeframe: {
          preset: "90d",
        },
        benchmarks: {
          primary: "BTC",
          secondary: "ETH",
          enabled: true,
        },
        recentFocuses: [],
        isAssistantPanelOpen: false,
        assistantPanelWidth: DEFAULT_PANEL_WIDTH,
        assistantPrefill: null,

        // ─── Focus Actions ───
        setFocus: (type, entityId, entityName) => {
          set((state) => {
            const newFocus: WorkspaceFocus = {
              type,
              entityId,
              entityName,
              entities: undefined,
            };

            // Add to recent focuses if valid
            let newRecentFocuses = state.recentFocuses;
            if (type && entityId && entityName) {
              const newRecent: RecentFocus = {
                type,
                entityId,
                entityName,
                timestamp: Date.now(),
              };

              // Remove duplicate if exists
              const filtered = state.recentFocuses.filter(
                (r) => !(r.type === type && r.entityId === entityId)
              );

              // Add to front and limit size
              newRecentFocuses = [newRecent, ...filtered].slice(0, MAX_RECENT_FOCUSES);
            }

            return {
              focus: newFocus,
              recentFocuses: newRecentFocuses,
            };
          });
        },

        setComparisonFocus: (type, entities, label) => {
          set({
            focus: {
              type,
              entityId: null,
              entityName: label ?? `Comparing ${entities.length} items`,
              entities,
            },
          });
        },

        clearFocus: () => {
          set({
            focus: {
              type: null,
              entityId: null,
              entityName: undefined,
              entities: undefined,
            },
          });
        },

        // ─── Timeframe Actions ───
        setTimeframe: (preset) => {
          set({
            timeframe: {
              preset,
              customStart: undefined,
              customEnd: undefined,
            },
          });
        },

        setCustomTimeframe: (start, end) => {
          set({
            timeframe: {
              preset: "all", // Use 'all' to indicate custom range
              customStart: start,
              customEnd: end,
            },
          });
        },

        // ─── Benchmark Actions ───
        setBenchmarks: (primary, secondary) => {
          set((state) => ({
            benchmarks: {
              ...state.benchmarks,
              primary,
              secondary,
            },
          }));
        },

        toggleBenchmarks: () => {
          set((state) => ({
            benchmarks: {
              ...state.benchmarks,
              enabled: !state.benchmarks.enabled,
            },
          }));
        },

        // ─── Assistant Panel Actions ───
        toggleAssistantPanel: () => {
          set((state) => ({
            isAssistantPanelOpen: !state.isAssistantPanelOpen,
          }));
        },

        setAssistantPanelOpen: (open) => {
          set({ isAssistantPanelOpen: open });
        },

        setAssistantPanelWidth: (width) => {
          const clampedWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, width));
          set({ assistantPanelWidth: clampedWidth });
        },

        // ─── Assistant Prefill Actions ───
        setAssistantPrefill: (text) => {
          set({ assistantPrefill: text });
        },

        clearAssistantPrefill: () => {
          set({ assistantPrefill: null });
        },

        openAssistantWithPrompt: (prompt) => {
          set({
            assistantPrefill: prompt,
            isAssistantPanelOpen: true,
          });
        },
      }),
      {
        name: "abi-workspace",
        // Only persist certain fields
        partialize: (state) => ({
          timeframe: state.timeframe,
          benchmarks: state.benchmarks,
          recentFocuses: state.recentFocuses,
          assistantPanelWidth: state.assistantPanelWidth,
        }),
      }
    ),
    { name: "WorkspaceStore" }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors (for optimized re-renders)
// ─────────────────────────────────────────────────────────────────────────────

export const selectFocus = (state: WorkspaceStore) => state.focus;
export const selectTimeframe = (state: WorkspaceStore) => state.timeframe;
export const selectBenchmarks = (state: WorkspaceStore) => state.benchmarks;
export const selectRecentFocuses = (state: WorkspaceStore) => state.recentFocuses;
export const selectIsAssistantPanelOpen = (state: WorkspaceStore) => state.isAssistantPanelOpen;
export const selectAssistantPanelWidth = (state: WorkspaceStore) => state.assistantPanelWidth;
export const selectAssistantPrefill = (state: WorkspaceStore) => state.assistantPrefill;
